import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { WHATSAPP_URL } from "../../i18n/translations";

export const Nav = ({ lang, setLang, t }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "about", label: t.nav.about },
    { id: "pricing", label: t.nav.pricing },
    { id: "team", label: t.nav.team },
    { id: "gallery", label: t.nav.gallery },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-zinc-950/85 backdrop-blur-xl border-b border-zinc-800/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        {/* Logo */}
        <button
          data-testid="logo-button"
          onClick={() => go("hero")}
          className="flex items-center gap-3 group"
        >
          <img
            src="/brand/tcars-logo.png"
            alt="T-CARS Garaje"
            className="h-9 md:h-11 w-auto object-contain"
          />
          <span className="hidden md:inline font-body text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-500 border-l border-zinc-800 pl-3">
            2019 S.L.
          </span>
        </button>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="link-anim font-body text-sm font-medium text-zinc-300 hover:text-zinc-50 transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 md:gap-3">
          <div
            data-testid="lang-toggle"
            className="hidden md:flex items-center border border-zinc-800 text-[11px] font-bold tracking-wider"
          >
            <button
              data-testid="lang-es"
              onClick={() => setLang("es")}
              className={`px-2.5 py-1.5 transition ${
                lang === "es"
                  ? "bg-zinc-50 text-zinc-950"
                  : "bg-transparent text-zinc-400 hover:text-zinc-50"
              }`}
            >
              ES
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2.5 py-1.5 transition ${
                lang === "en"
                  ? "bg-zinc-50 text-zinc-950"
                  : "bg-transparent text-zinc-400 hover:text-zinc-50"
              }`}
            >
              EN
            </button>
          </div>

          <a
            data-testid="nav-whatsapp-cta"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-zinc-950 font-body font-semibold text-sm px-4 py-2 transition-colors"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2.4} />
            {t.nav.whatsapp}
          </a>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-zinc-200"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-zinc-950 border-t border-zinc-800/60"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => go(l.id)}
                className="text-left font-heading text-2xl font-medium tracking-tight text-zinc-100"
              >
                {l.label}
              </button>
            ))}
            <div className="pt-4 flex items-center gap-3">
              <div className="flex border border-zinc-800 text-[11px] font-bold tracking-wider">
                <button
                  onClick={() => setLang("es")}
                  className={`px-3 py-2 ${
                    lang === "es" ? "bg-zinc-50 text-zinc-950" : "text-zinc-400"
                  }`}
                >
                  ES
                </button>
                <button
                  onClick={() => setLang("en")}
                  className={`px-3 py-2 ${
                    lang === "en" ? "bg-zinc-50 text-zinc-950" : "text-zinc-400"
                  }`}
                >
                  EN
                </button>
              </div>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-red-600 text-zinc-950 font-semibold text-sm px-4 py-2"
              >
                <MessageCircle className="w-4 h-4" /> {t.nav.whatsapp}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
